import React from 'react'
import { useHistory } from 'react-router-dom'
import { AppState, Auth0Provider } from '@auth0/auth0-react'
import { auth0Config, isOnPrem, isExternalDemo } from '../config'

interface ServiceProvidersProps {
  children: React.ReactNode
}

/**
 * ServiceProviders
 *
 * Conditionally wraps the application with Auth0Provider for cloud deployments.
 * For on-prem and external demo modes, just renders children directly.
 *
 * All services (Auth, FeatureFlags, Analytics, Monitoring) are now accessed via
 * hooks (useAuth, useFeatureFlags, useAnalytics, useMonitoring) instead of context providers.
 */
export function ServiceProviders({ children }: ServiceProvidersProps): JSX.Element {
  const history = useHistory()

  const onRedirectCallback = (appState?: AppState | undefined) => {
    history.push(appState?.returnTo || window.location.pathname)
  }

  const getOrgId = () => {
    return localStorage.getItem('orgId') || undefined
  }

  // For on-prem and external demo modes, no Auth0Provider needed
  if (isOnPrem || isExternalDemo) {
    return <>{children}</>
  }

  // For cloud mode, wrap with Auth0Provider
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      audience="https://api.deepcell.com"
      redirectUri={window.location.origin}
      onRedirectCallback={onRedirectCallback}
      organization={getOrgId()}
    >
      {children}
    </Auth0Provider>
  )
}

export default ServiceProviders
